import {
  useEffect,
  useMemo,
  useState,
  type DragEvent as ReactDragEvent,
  type ReactNode,
} from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { printHokstickers, type HokSticker } from "../components/printHokstickers";
import {
  DateField,
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  GearIcon,
  PencilIcon,
  PlusCircleSolidIcon,
} from "@fawkes/ui";

import { todayIso } from "../i18n/format";
import { useModuleAvailability } from "../api/modules";
import { useAanraken } from "../useMobiel";
import { fetchKennelAvatarObjectUrl } from "../api/kennelAvatar";
import {
  KennelGroepFormDialog,
  RuimteTypeFormDialog,
  type RoomEditTarget,
  type RoomTypeEditTarget,
} from "./kennelplanningDialogs";
import { KennelTimeline } from "./KennelTimeline";
import { Drawer } from "./kennelplanning/Drawer";
import { StayHistoryDrawer } from "./kennelplanning/StayHistoryDrawer";
import {
  FieldSettingsDrawer,
  DEFAULT_VISIBLE_FIELDS,
} from "./kennelplanning/FieldSettingsDrawer";
import "./Kennelplanning.css";

// kp-Epic 2 (kennelplanning-migration): the React floorplan read SHELL. A new SPA Page that renders
// the Jmix `KennelplanningOverviewView` floorplan / list / capacity panel read-only, at visual
// parity, for the current vestiging on TODAY. The Jmix `kennelplanning` route stays live (strangler);
// this is a SEPARATE nav item so both coexist during the migration.
//
// Backend is built in parallel (kp-Epic 1). We code to the contract in the epic/stories and read via
// Refine `useCustom` against RELATIVE urls (the dataProvider prepends `/api`):
//   GET kennelplanning/overview?datum=&species=  → { groepen:[{ groep, ruimteType, bezet, totaal,
//       tiles:[{ kennel, occupants:[occupant], occupantCount, occupied, overCapacity,
//       effectiveMaxCapaciteit }] }], pendingAnimals:[pending] }
//   GET kennelplanning/room-types                → [{ id, code, naam, kleur, volgorde, maxCapaciteit }]
//   GET kennelplanning/capacity?species=         → { bezet, totaal, percentage, perGroep:[...],
//       prognoseDagenTotVol, prognoseIntake, prognoseRelease, prognoseNetto, prognoseTrend, ... }
// Everything uses defensive optional chaining so it renders with partial/empty data, and the screen
// typechecks/builds + shows an empty/loading state even when the live endpoint isn't up yet.
//
// Out of scope for this story (later epics): drag-drop/placement writes (kp-Epic 3), room/room-type
// dialogs (kp-Epic 4), real prognose numbers + suggestions (kp-Epic 5), the timeline canvas
// (kp-Epic 6), the drawers + per-user field-preference persistence (kp-Epic 7). Timeline & List
// toggles render; Timeline is a "binnenkort" stub. The default visible-field set is static here
// (PHOTO, NAME, KENNEL_NUM, GENDER, DAYS, FLAGS) until kp-Epic 7 supplies user field-prefs.

// ── Wire contract types (NFR7: Integer ids; Long dierId; LocalDate as ISO strings) ────────────────

type SpeciesFilter = "HONDEN" | "KATTEN" | "OVERIG";
type ViewMode = "FLOORPLAN" | "TIMELINE" | "LIST";

// kp-Epic 6: the timeline date-bar mode. DATE = a single date mapped to a window; PERIODE = an explicit
// from…to range (swap if reversed). The window feeds the KennelTimeline fetch.
type DateMode = "DATE" | "PERIODE";

/** Shift an ISO date (YYYY-MM-DD) by a whole number of days, returning ISO. */
function shiftISODate(iso: string, deltaDays: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0));
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** A room-type reference row — `kennelplanning/room-types`. */
interface RoomType {
  id?: number | null;
  code?: string | null;
  naam?: string | null;
  /** Free-form colour token (green/red/orange/blue/grey or other) — see D-decisions. */
  kleur?: string | null;
  volgorde?: number | null;
  maxCapaciteit?: number | null;
  /** kp-Epic 4: carried by the feed so the editor can round-trip them. */
  beschrijving?: string | null;
  version?: number | null;
}

/** Per-occupant DTO — carries every field the visible-field chips can render (FR3). */
export interface Occupant {
  dierId?: number | null;
  asielVerblijfId?: number | null;
  naam?: string | null;
  geslacht?: string | null;
  gecastreerd?: boolean | null;
  chipPasnummer?: string | null;
  rasNaam?: string | null;
  diersoortNaam?: string | null;
  daysInShelter?: number | null;
  opnameDatum?: string | null;
  /** M-flag input: non-blank → medical flag. */
  medischAlgemeen?: string | null;
  /** !-flag input. */
  betrokkenBijIncident?: boolean | null;
  /** D-flag input: present → diet flag. */
  voedingCategorie?: string | null;
  onlineDierAfbeeldingUrl?: string | null;
  // kp-Epic 7 (part 2): extra identity/status/verblijf fields for the preference-driven chips.
  tatoeage?: string | null;
  paspoortNummer?: string | null;
  stamboomNummer?: string | null;
  kenmerken?: string | null;
  bijzonderheden?: string | null;
  zindelijk?: boolean | null;
  drinktUitFles?: boolean | null;
  geboortedatum?: string | null;
  kruising?: boolean | null;
  kruisingNaam?: string | null;
  vachtNaam?: string | null;
  kleurNaam?: string | null;
  plaatsbaar?: boolean | null;
  asielOpnameSoort?: string | null;
  vertrekDatum?: string | null;
  verblijfNummer?: string | null;
  halsbandNummer?: string | null;
  gereserveerdTotDatum?: string | null;
  opmerkingIn?: string | null;
  // kp-Epic 7 (final): medisch chips — latest weight + latest vaccination (contract additions).
  gewichtWaarde?: number | null;
  gewichtDatum?: string | null;
  entingNaam?: string | null;
  entingDatum?: string | null;
}

/** Pending (to-place) animal — same identity payload as an occupant, plus its verblijf id. */
type PendingAnimal = Occupant;

/** A kennel tile within a room. */
interface KennelTile {
  kennelAsielId?: number | null;
  kennelNummer?: string | null;
  occupants?: Occupant[] | null;
  occupantCount?: number | null;
  occupied?: boolean | null;
  overCapacity?: boolean | null;
  /** kennel max else group max else null. */
  effectiveMaxCapaciteit?: number | null;
}

/** A room (KennelGroepAsiel) card. */
interface KennelGroup {
  kennelGroepAsielId?: number | null;
  naam?: string | null;
  /** String code joined to RuimteType by `code` for display colour/label (no FK). */
  ruimteTypeCode?: string | null;
  /** Enum id (STANDARD/QUARANTINE/ISOLATION/PUPPY/KITTEN/OVERIG/OTHER) for the fallback. */
  ruimteTypeEnumId?: string | null;
  bezet?: number | null;
  totaal?: number | null;
  tiles?: KennelTile[] | null;
}

interface OverviewResponse {
  groepen?: KennelGroup[] | null;
  pendingAnimals?: PendingAnimal[] | null;
}

/** Per-room capacity row in the right panel. */
interface RoomCapacityRow {
  kennelGroepAsielId?: number | null;
  naam?: string | null;
  ruimteTypeCode?: string | null;
  ruimteTypeEnumId?: string | null;
  bezet?: number | null;
  totaal?: number | null;
  /** kp-Epic 4: the feed emits these so the room editor can round-trip them. */
  version?: number | null;
  positie?: number | null;
}

interface CapacityResponse {
  bezet?: number | null;
  totaal?: number | null;
  percentage?: number | null;
  perGroep?: RoomCapacityRow[] | null;
  /** kp-Epic 5: estimated days until full. 0 → already full; >0 → "{n} dagen vol";
      null → "Geen prognose" (signal too weak — but the basis line still renders). */
  prognoseDagenTotVol?: number | null;
  /** 28-day basis figures — always populated so the "Op basis van …" line renders even when
      `prognoseDagenTotVol` is null. */
  prognoseIntake?: number | null;
  prognoseRelease?: number | null;
  prognoseNetto?: number | null;
  /** STABIEL / STIJGEND / DALEND — mapped to a Dutch label via i18n. */
  prognoseTrend?: string | null;
}

/** kp-Epic 5: a single placement suggestion — `kennelplanning/suggestions?asielVerblijfId=`. */
interface Suggestion {
  kennelAsielId?: number | null;
  kennelNummer?: string | null;
  kennelGroepAsielId?: number | null;
  roomNaam?: string | null;
  score?: number | null;
  motivation?: string | null;
}

/** The Refine translate signature (positional %s/%d args via `{ args: [...] }`). */
type Translate = (key: string, options?: { args?: unknown[] }, defaultMessage?: string) => string;

// ── kp-Epic 3: drag-drop placement (additive) ─────────────────────────────────────────────────────
// HTML5 DnD parity with the Jmix board. An occupant (kennel or pending) is dragged onto a kennel tile
// (PLACE from pending / MOVE between kennels) or back onto the pending strip (RELEASE). The payload is
// JSON in dataTransfer; a module-level ref is the fallback for browsers that don't expose getData during
// dragover. Writes go through the kp-Epic 1 contract; the SPA refetches the overview (+capacity) on
// success rather than reading the mutation body. Editing is gated by `editable` (today + no trace).

/** What a drag carries: the verblijf being moved and where it came from. */
export interface DragPayload {
  asielVerblijfId: number;
  source: "kennel" | "pending";
}

const DRAG_MIME = "application/x-fawkes-kennelplanning";
/** Fallback for the payload when dataTransfer.getData is unavailable (e.g. during dragover). */
let dragPayloadRef: DragPayload | null = null;

export function readDragPayload(e: ReactDragEvent): DragPayload | null {
  try {
    const raw = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData("text/plain");
    if (raw) return JSON.parse(raw) as DragPayload;
  } catch {
    // fall through to the module-level ref
  }
  return dragPayloadRef;
}

function setDragPayload(e: ReactDragEvent, payload: DragPayload): void {
  dragPayloadRef = payload;
  const json = JSON.stringify(payload);
  try {
    e.dataTransfer.setData(DRAG_MIME, json);
    e.dataTransfer.setData("text/plain", json);
    e.dataTransfer.effectAllowed = "move";
  } catch {
    // the module-level ref still carries the payload
  }
}

/** An inline success/error notice (mirrors the ReleaseWizard Notice pattern; there is no toast provider). */
type DndNotice = { kind: "ok" | "error"; text: string } | null;

/** Drop-handler callbacks threaded down to the tiles / pending strip (only set when `editable`). */
/**
 * Tik-om-te-verplaatsen: de telefoonvariant van slepen.
 *
 * Een dier in een hok zetten gaat op de desktop door het kaartje naar een hok te slepen.
 * Op een aanraakscherm bestaat slepen niet — HTML5-drag luistert naar de muis — en daarmee
 * was de kern van dit scherm op een telefoon onbereikbaar: je kon alles zien en niets doen.
 * Daarom hier dezelfde handeling in twee tikken: eerst het dier oppakken, dan het hok
 * aanwijzen. Zolang er een dier "in de hand" is licht elk hok op als doel en staat er een
 * balk in beeld met wie je vasthebt en de mogelijkheid om los te laten.
 *
 * Deze knoppen bestaan alleen onder 768 pixels (zie useMobiel); op de desktop wordt er niets
 * extra's getekend en verandert er niets aan het slepen.
 */
interface VerplaatsHandlers {
  /** Het dier dat nu vastgehouden wordt, of null. */
  opgepakt: { asielVerblijfId: number; naam: string; bron: "kennel" | "pending" } | null;
  oppakken: (asielVerblijfId: number, naam: string, bron: "kennel" | "pending") => void;
  plaatsIn: (kennelAsielId: number) => void;
  losLaten: () => void;
  annuleren: () => void;
  /** Opschrift van de oppakknop ("Verplaats"). */
  label: string;
}

interface DndHandlers {
  onOccupantDragStart: (e: ReactDragEvent, asielVerblijfId: number, source: "kennel" | "pending") => void;
  onTileDrop: (e: ReactDragEvent, kennelAsielId: number) => void;
  onPendingDrop: (e: ReactDragEvent) => void;
}

// ── Colour / label resolution (ports the Jmix resolveKleurForGroep / enum fallback) ───────────────

const COLOUR_TOKENS = ["green", "red", "orange", "blue", "grey"] as const;
type ColourToken = (typeof COLOUR_TOKENS)[number];

export function normalizeColour(raw: string | null | undefined): ColourToken | null {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  return (COLOUR_TOKENS as readonly string[]).includes(lower) ? (lower as ColourToken) : null;
}

/** The Jmix enum→colour fallback used when no RuimteType row matches the group's code. */
export function enumColourFallback(enumId: string | null | undefined): ColourToken {
  switch ((enumId ?? "").toUpperCase()) {
    case "QUARANTINE":
      return "red";
    case "ISOLATION":
      return "orange";
    case "PUPPY":
    case "KITTEN":
      return "blue";
    case "OVERIG":
    case "OTHER":
      return "grey";
    default:
      return "green";
  }
}

/** Two-layer resolution: room-type feed colour → enum fallback. */
function resolveGroupColour(
  group: KennelGroup,
  roomTypeByCode: Map<string, RoomType>,
): ColourToken {
  const code = group.ruimteTypeCode ?? undefined;
  if (code) {
    const rt = roomTypeByCode.get(code);
    const fromFeed = normalizeColour(rt?.kleur);
    if (fromFeed) return fromFeed;
  }
  return enumColourFallback(group.ruimteTypeEnumId);
}

/** The uppercase short type-badge label: room-type naam else the enum short label. */
function resolveTypeLabel(
  group: KennelGroup,
  roomTypeByCode: Map<string, RoomType>,
  t: (k: string) => string,
): string {
  const code = group.ruimteTypeCode ?? undefined;
  if (code) {
    const rt = roomTypeByCode.get(code);
    if (rt?.naam) return rt.naam.toUpperCase();
  }
  const enumId = (group.ruimteTypeEnumId ?? "").toUpperCase();
  if (!enumId) return "";
  const known = ["STANDARD", "QUARANTINE", "ISOLATION", "PUPPY", "KITTEN", "OTHER"];
  const key = known.includes(enumId) ? enumId : enumId === "OVERIG" ? "OTHER" : null;
  return key ? t(`kennelplanning.ruimtetype.short.${key}`) : enumId;
}

// ── Gender ring colour + flags + helpers (ported verbatim from the Jmix view) ─────────────────────

/** computeAvatarBorderColor — hardcoded DATA colours (gender/cas), not theme colours (Jmix parity). */
function computeAvatarBorderColor(o: Occupant | null): string {
  if (!o) return "#8a9085";
  const gender = o.geslacht;
  const cas = o.gecastreerd === true;
  if (gender == null) return "#7a8c5e";
  const upper = gender.toUpperCase();
  if (upper.startsWith("M")) return cas ? "#3e6a87" : "#5a8aab";
  if (upper.startsWith("V") || upper.startsWith("F")) return cas ? "#8e4a70" : "#b76a90";
  return "#7a8c5e";
}

function genderSymbol(gender: string | null | undefined): string {
  if (!gender) return "?";
  const upper = gender.toUpperCase();
  if (upper.startsWith("M")) return "♂";
  if (upper.startsWith("V") || upper.startsWith("F")) return "♀";
  return "?";
}

function initialFor(naam: string | null | undefined): string {
  if (!naam || !naam.trim()) return "?";
  return naam.trim().charAt(0).toUpperCase();
}

/** M / ! / D flags exactly as Jmix `buildAnimalFlags`. */
function animalFlags(o: Occupant | null | undefined): Array<{ kind: "med" | "bite" | "diet"; label: string }> {
  const flags: Array<{ kind: "med" | "bite" | "diet"; label: string }> = [];
  if (!o) return flags;
  if (o.medischAlgemeen && o.medischAlgemeen.trim()) flags.push({ kind: "med", label: "M" });
  if (o.betrokkenBijIncident === true) flags.push({ kind: "bite", label: "!" });
  if (o.voedingCategorie != null && String(o.voedingCategorie).trim()) flags.push({ kind: "diet", label: "D" });
  return flags;
}

// ── Avatar component (bearer-aware blob → object URL; fallback online URL then initials) ───────────

function Avatar({ occupant, sizeClass }: { occupant: Occupant | null | undefined; sizeClass: string }) {
  const dierId = occupant?.dierId ?? null;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    let created: string | null = null;
    setFailed(false);
    setObjectUrl(null);
    if (dierId != null) {
      void fetchKennelAvatarObjectUrl(dierId).then((url) => {
        if (!live) return;
        created = url;
        setObjectUrl(url);
      });
    }
    return () => {
      live = false;
      // The object URL is cached/owned by kennelAvatar.ts (shared per dier), so we do NOT revoke it
      // here — revoking would break other tiles of the same dier still using it.
      void created;
    };
  }, [dierId]);

  const borderColor = computeAvatarBorderColor(occupant ?? null);
  // Source order (Jmix): DB image (objectUrl) → onlineDierAfbeeldingUrl → initials.
  const src = objectUrl ?? (occupant?.onlineDierAfbeeldingUrl || null);
  const showImg = !!src && !failed;

  return (
    <div
      className={`fawkes-kennelplanning__avatar ${sizeClass}`}
      style={{ ["--kp-gender-color" as string]: borderColor }}
    >
      {showImg ? (
        <img
          className="fawkes-kennelplanning__avatar-img"
          src={src ?? undefined}
          alt={occupant?.naam ?? ""}
          onError={() => setFailed(true)}
        />
      ) : (
        <span className="fawkes-kennelplanning__avatar-fallback">{initialFor(occupant?.naam)}</span>
      )}
    </div>
  );
}

// ── Detail chips: the default visible set (PHOTO/NAME/KENNEL_NUM/GENDER/DAYS/FLAGS) ────────────────
// Field-prefs are a later epic (kp-Epic 7); here the tile renders NAME (always), the GENDER chip, the
// DAYS chip and the FLAGS — driven by the static default set. PHOTO/KENNEL_NUM are the avatar + the
// number badge respectively.

function FlagBadges({ occupant, className }: { occupant: Occupant | null | undefined; className?: string }) {
  const flags = animalFlags(occupant);
  if (flags.length === 0) return null;
  return (
    <div className={`fawkes-kennelplanning__tile-flags ${className ?? ""}`}>
      {flags.map((f) => (
        <span
          key={f.kind}
          className={`fawkes-kennelplanning__flag fawkes-kennelplanning__flag--${f.kind}`}
        >
          {f.label}
        </span>
      ))}
    </div>
  );
}

// ── Preference-driven occupant chips (kp-Epic 7 part 2) ─────────────────────────────────────────────
// One shared builder used by every chip renderer (SingleOccupant, OccupantMiniCard, the pending card and
// the list detail). It returns the inline "extra" chip nodes for the keys that are present in
// `visibleFields` AND have a value. NAME/PHOTO/KENNEL_NUM keep their special placement (title/avatar/
// number) and are NOT emitted here; FLAGS keeps its <FlagBadges> placement and is gated separately by
// the caller. GEWICHT/ENTING have no data on the contract yet, so they intentionally render nothing.

/** Format an ISO date (YYYY-MM-DD or full ISO) as DD-MM-YYYY; passthrough when unparseable. */
export function formatISODate(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Derive a compact age label ("3j" / "5m") from an ISO birth date. Returns null when unparseable. */
export function deriveAgeLabel(geboortedatum: string | null | undefined, t: (k: string) => string): string | null {
  if (!geboortedatum) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(geboortedatum);
  if (!m) return null;
  const birth = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(birth.getTime())) return null;
  const now = new Date();
  let months =
    (now.getUTCFullYear() - birth.getUTCFullYear()) * 12 + (now.getUTCMonth() - birth.getUTCMonth());
  if (now.getUTCDate() < birth.getUTCDate()) months -= 1;
  if (months < 0) months = 0;
  if (months >= 12) {
    const years = Math.floor(months / 12);
    return `${years}${t("kennelplanning.fieldSettings.unit.years")}`;
  }
  return `${months}${t("kennelplanning.fieldSettings.unit.months")}`;
}

/** A single inline chip node. */
function fieldChip(key: string, content: string): ReactNode {
  return (
    <span key={key} className="fawkes-kennelplanning__field-item">
      {content}
    </span>
  );
}

/**
 * Build the inline "extra" chip nodes for an occupant, gated by the user's visible-field set.
 * Only keys that are BOTH in `visibleFields` AND have a present value emit a chip.
 */
export function renderOccupantFields(
  o: Occupant,
  visibleFields: Set<string>,
  t: (k: string) => string,
): ReactNode[] {
  const chips: ReactNode[] = [];
  const has = (key: string) => visibleFields.has(key);
  const nonBlank = (v: string | null | undefined): v is string => v != null && String(v).trim() !== "";

  // ── Identiteit ──
  // acc prints the chip number with a leading # so it reads as a number and not as a stray digit run.
  if (has("ID") && nonBlank(o.chipPasnummer)) chips.push(fieldChip("ID", `#${o.chipPasnummer!}`));
  if (has("PASPOORT") && nonBlank(o.paspoortNummer)) chips.push(fieldChip("PASPOORT", o.paspoortNummer!));
  if (has("STAMBOOM") && nonBlank(o.stamboomNummer)) chips.push(fieldChip("STAMBOOM", o.stamboomNummer!));
  if (has("TATOEAGE") && nonBlank(o.tatoeage)) chips.push(fieldChip("TATOEAGE", o.tatoeage!));
  if (has("SPECIES") && nonBlank(o.diersoortNaam)) chips.push(fieldChip("SPECIES", o.diersoortNaam!));
  if (has("RAS") && nonBlank(o.rasNaam)) chips.push(fieldChip("RAS", o.rasNaam!));
  if (has("KRUISING") && o.kruising === true && nonBlank(o.kruisingNaam))
    chips.push(fieldChip("KRUISING", o.kruisingNaam!));
  if (has("VACHT") && nonBlank(o.vachtNaam)) chips.push(fieldChip("VACHT", o.vachtNaam!));
  if (has("KLEUR") && nonBlank(o.kleurNaam)) chips.push(fieldChip("KLEUR", o.kleurNaam!));
  if (has("KENMERKEN") && nonBlank(o.kenmerken)) chips.push(fieldChip("KENMERKEN", o.kenmerken!));
  if (has("BIJZONDERHEDEN") && nonBlank(o.bijzonderheden))
    chips.push(fieldChip("BIJZONDERHEDEN", o.bijzonderheden!));

  // ── Status ──
  if (has("GENDER") && nonBlank(o.geslacht))
    chips.push(
      <span
        key="GENDER"
        className="fawkes-kennelplanning__field-item fawkes-kennelplanning__field--gender"
      >
        {genderSymbol(o.geslacht)}
      </span>,
    );
  if (has("CASTRATED") && o.gecastreerd === true)
    chips.push(fieldChip("CASTRATED", t("kennelplanning.fieldSettings.value.castrated")));
  if (has("LEEFTIJD")) {
    const age = deriveAgeLabel(o.geboortedatum, t);
    if (age) chips.push(fieldChip("LEEFTIJD", age));
  }
  if (has("GEBOORTE") && nonBlank(o.geboortedatum))
    chips.push(fieldChip("GEBOORTE", formatISODate(o.geboortedatum)));
  if (has("PLAATSBAAR") && o.plaatsbaar === true)
    chips.push(fieldChip("PLAATSBAAR", t("kennelplanning.fieldSettings.value.plaatsbaar")));
  if (has("ZINDELIJK") && o.zindelijk === true)
    chips.push(fieldChip("ZINDELIJK", t("kennelplanning.fieldSettings.value.zindelijk")));
  if (has("DRINKT_FLES") && o.drinktUitFles === true)
    chips.push(fieldChip("DRINKT_FLES", t("kennelplanning.fieldSettings.value.drinktFles")));

  // ── Verblijf ──
  if (has("DAYS") && o.daysInShelter != null)
    chips.push(fieldChip("DAYS", `${o.daysInShelter}${t("kennelplanning.fieldSettings.unit.days")}`));
  if (has("OPNAME") && nonBlank(o.opnameDatum)) chips.push(fieldChip("OPNAME", formatISODate(o.opnameDatum)));
  if (has("OPNAME_SOORT") && nonBlank(o.asielOpnameSoort))
    chips.push(fieldChip("OPNAME_SOORT", o.asielOpnameSoort!));
  if (has("VERTREK_DATUM") && nonBlank(o.vertrekDatum))
    chips.push(fieldChip("VERTREK_DATUM", formatISODate(o.vertrekDatum)));
  if (has("VERBLIJF_NUMMER") && nonBlank(o.verblijfNummer))
    chips.push(fieldChip("VERBLIJF_NUMMER", o.verblijfNummer!));
  if (has("HALSBAND") && nonBlank(o.halsbandNummer)) chips.push(fieldChip("HALSBAND", o.halsbandNummer!));
  if (has("GERESERVEERD_TOT") && nonBlank(o.gereserveerdTotDatum))
    chips.push(fieldChip("GERESERVEERD_TOT", formatISODate(o.gereserveerdTotDatum)));
  if (has("OPMERKING_IN") && nonBlank(o.opmerkingIn)) chips.push(fieldChip("OPMERKING_IN", o.opmerkingIn!));

  // ── Medisch ──
  // FLAGS keeps its <FlagBadges> placement (gated by the caller). GEWICHT/ENTING render real data
  // when the contract supplies it (kp-Epic 7 final); both fall back to nothing when absent.
  if (has("GEWICHT") && o.gewichtWaarde != null) {
    const datum = formatISODate(o.gewichtDatum);
    const kg = `${o.gewichtWaarde} ${t("kennelplanning.fieldSettings.unit.kg")}`;
    chips.push(fieldChip("GEWICHT", datum ? `${kg} · ${datum}` : kg));
  }
  if (has("ENTING") && nonBlank(o.entingNaam)) {
    const datum = formatISODate(o.entingDatum);
    chips.push(fieldChip("ENTING", datum ? `${o.entingNaam} · ${datum}` : o.entingNaam!));
  }

  return chips;
}

/** Het knopje waarmee je een dier oppakt om het ergens anders neer te zetten (alleen mobiel). */
function OppakKnop({
  verplaats,
  asielVerblijfId,
  naam,
  bron,
}: {
  verplaats?: VerplaatsHandlers;
  asielVerblijfId: number | null;
  naam: string;
  bron: "kennel" | "pending";
}) {
  if (!verplaats || asielVerblijfId == null) return null;
  const vast = verplaats.opgepakt?.asielVerblijfId === asielVerblijfId;
  return (
    <button
      type="button"
      className={
        "fawkes-kennelplanning__oppak" + (vast ? " fawkes-kennelplanning__oppak--vast" : "")
      }
      onClick={(e) => {
        e.stopPropagation();
        if (vast) verplaats.annuleren();
        else verplaats.oppakken(asielVerblijfId, naam, bron);
      }}
    >
      {verplaats.label}
    </button>
  );
}

// ── Kennel tile ───────────────────────────────────────────────────────────────────────────────────

function Tile({
  tile,
  t,
  visibleFields,
  dnd,
  verplaats,
  suggested,
  suggestedBest,
  onOpenDrawer,
}: {
  tile: KennelTile;
  t: (k: string) => string;
  /** kp-Epic 7 (part 2): the user's visible-field set, driving every occupant chip. */
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  /** Tik-om-te-verplaatsen (alleen op telefoonformaat). */
  verplaats?: VerplaatsHandlers;
  /** kp-Epic 5: this empty kennel is among the AI suggestions. */
  suggested?: boolean;
  /** kp-Epic 5: the top suggestion — pulses + scrolls into view. */
  suggestedBest?: boolean;
  /** kp-Epic 7: click an occupant → open the stay-history drawer for that dier. */
  onOpenDrawer?: (dierId: number) => void;
}) {
  const occupants = tile.occupants ?? [];
  const count = tile.occupantCount ?? occupants.length;
  const occupied = tile.occupied ?? count > 0;
  const max = tile.effectiveMaxCapaciteit ?? null;
  const overCapacity = tile.overCapacity ?? (max != null && count > max);
  // Jmix: badge shows when occupantCount > 1 OR (max != null && occupied).
  const showOccupancy = count > 1 || (max != null && occupied);
  const kennelAsielId = tile.kennelAsielId ?? null;
  const [dropActive, setDropActive] = useState(false);

  // Scroll the top suggestion into view once it becomes the best match.
  const tileRef = (el: HTMLDivElement | null) => {
    if (el && suggestedBest) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const cls = [
    "fawkes-kennelplanning__tile",
    !occupied ? "fawkes-kennelplanning__tile--empty" : "fawkes-kennelplanning__tile--occupied",
    occupied && overCapacity ? "fawkes-kennelplanning__tile--over-capacity" : "",
    occupied && occupants.length === 1 ? "fawkes-kennelplanning__tile--single" : "",
    occupied && occupants.length > 1 ? "fawkes-kennelplanning__tile--multi" : "",
    dropActive ? "fawkes-kennelplanning__tile--drop-active" : "",
    suggested ? "fawkes-kennelplanning__tile--suggested" : "",
    suggestedBest ? "fawkes-kennelplanning__tile--suggested-best" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // A kennel tile is a drop target only when editing is on and it has a target kennel id.
  const dropProps =
    dnd && kennelAsielId != null
      ? {
          onDragOver: (e: ReactDragEvent) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = "move";
            if (!dropActive) setDropActive(true);
          },
          onDragLeave: () => setDropActive(false),
          onDrop: (e: ReactDragEvent) => {
            setDropActive(false);
            dnd.onTileDrop(e, kennelAsielId);
          },
        }
      : {};

  // Met een dier in de hand is elk hok een doel: tikken zet het dier hier neer.
  const wachtOpDoel = verplaats?.opgepakt != null && kennelAsielId != null;
  const doelProps = wachtOpDoel
    ? {
        onClick: () => verplaats!.plaatsIn(kennelAsielId!),
        role: "button",
        tabIndex: 0,
      }
    : {};

  return (
    <div
      className={cls + (wachtOpDoel ? " fawkes-kennelplanning__tile--doel" : "")}
      ref={tileRef}
      {...dropProps}
      {...doelProps}
    >
      {visibleFields.has("KENNEL_NUM") && (
        <span className="fawkes-kennelplanning__tile-number">{tile.kennelNummer ?? ""}</span>
      )}
      {suggested && (
        <span className="fawkes-kennelplanning__tile-suggested-badge">
          {t("kennelplanning.suggestion.badge")}
        </span>
      )}
      {showOccupancy && (
        <span className="fawkes-kennelplanning__tile-occupancy">
          {count}/{max == null ? "-" : max}
        </span>
      )}

      {!occupied ? (
        <span className="fawkes-kennelplanning__tile-empty-label">{t("kennelplanning.tile.empty")}</span>
      ) : occupants.length === 1 ? (
        <SingleOccupant
          occupant={occupants[0]}
          t={t}
          visibleFields={visibleFields}
          dnd={dnd}
          verplaats={verplaats}
          onOpenDrawer={onOpenDrawer}
        />
      ) : (
        <div className="fawkes-kennelplanning__tile-occupants">
          {occupants.map((o, i) => (
            <OccupantMiniCard
              key={o.asielVerblijfId ?? o.dierId ?? i}
              occupant={o}
              t={t}
              visibleFields={visibleFields}
              dnd={dnd}
              verplaats={verplaats}
              onOpenDrawer={onOpenDrawer}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SingleOccupant({
  occupant,
  t,
  visibleFields,
  dnd,
  verplaats,
  onOpenDrawer,
}: {
  occupant: Occupant;
  t: (k: string) => string;
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  verplaats?: VerplaatsHandlers;
  onOpenDrawer?: (dierId: number) => void;
}) {
  const asielVerblijfId = occupant.asielVerblijfId ?? null;
  const draggable = dnd != null && asielVerblijfId != null;
  const dierId = occupant.dierId ?? null;
  // Met een dier in de hand hoort een tik bij het hok eronder (neerzetten), niet bij het dossier.
  const clickable = onOpenDrawer != null && dierId != null && verplaats?.opgepakt == null;
  const chips = renderOccupantFields(occupant, visibleFields, t);
  return (
    <div
      className={
        "fawkes-kennelplanning__single-body" +
        (draggable ? " fawkes-kennelplanning__draggable" : "") +
        (clickable ? " fawkes-kennelplanning__clickable" : "")
      }
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (e: ReactDragEvent) => dnd!.onOccupantDragStart(e, asielVerblijfId!, "kennel")
          : undefined
      }
      onClick={clickable ? () => onOpenDrawer!(dierId!) : undefined}
    >
      {visibleFields.has("PHOTO") && (
        <Avatar occupant={occupant} sizeClass="fawkes-kennelplanning__avatar--tile" />
      )}
      <div className="fawkes-kennelplanning__tile-content">
        {visibleFields.has("NAME") && (
          <span className="fawkes-kennelplanning__tile-name">{occupant.naam ?? ""}</span>
        )}
        {chips.length > 0 && <div className="fawkes-kennelplanning__tile-fields">{chips}</div>}
        {visibleFields.has("FLAGS") && <FlagBadges occupant={occupant} />}
      </div>
      <OppakKnop
        verplaats={verplaats}
        asielVerblijfId={asielVerblijfId}
        naam={occupant.naam ?? ""}
        bron="kennel"
      />
    </div>
  );
}

function OccupantMiniCard({
  occupant,
  t,
  visibleFields,
  dnd,
  verplaats,
  onOpenDrawer,
}: {
  occupant: Occupant;
  t: (k: string) => string;
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  verplaats?: VerplaatsHandlers;
  onOpenDrawer?: (dierId: number) => void;
}) {
  const asielVerblijfId = occupant.asielVerblijfId ?? null;
  const draggable = dnd != null && asielVerblijfId != null;
  const dierId = occupant.dierId ?? null;
  // Met een dier in de hand hoort een tik bij het hok eronder (neerzetten), niet bij het dossier.
  const clickable = onOpenDrawer != null && dierId != null && verplaats?.opgepakt == null;
  const chips = renderOccupantFields(occupant, visibleFields, t);
  return (
    <div
      className={
        "fawkes-kennelplanning__occupant" +
        (draggable ? " fawkes-kennelplanning__draggable" : "") +
        (clickable ? " fawkes-kennelplanning__clickable" : "")
      }
      draggable={draggable || undefined}
      onDragStart={
        draggable
          ? (e: ReactDragEvent) => dnd!.onOccupantDragStart(e, asielVerblijfId!, "kennel")
          : undefined
      }
      onClick={clickable ? () => onOpenDrawer!(dierId!) : undefined}
    >
      {visibleFields.has("NAME") && (
        <span className="fawkes-kennelplanning__occupant-name">{occupant.naam ?? ""}</span>
      )}
      {chips.length > 0 && <div className="fawkes-kennelplanning__occupant-meta">{chips}</div>}
      {visibleFields.has("FLAGS") && (
        <FlagBadges occupant={occupant} className="fawkes-kennelplanning__occupant-flags" />
      )}
      <OppakKnop
        verplaats={verplaats}
        asielVerblijfId={asielVerblijfId}
        naam={occupant.naam ?? ""}
        bron="kennel"
      />
    </div>
  );
}

// ── Group card ────────────────────────────────────────────────────────────────────────────────────

function GroupCard({
  group,
  roomTypeByCode,
  t,
  visibleFields,
  dnd,
  verplaats,
  suggestedKennelIds,
  bestKennelId,
  onOpenDrawer,
}: {
  group: KennelGroup;
  roomTypeByCode: Map<string, RoomType>;
  t: (k: string) => string;
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  verplaats?: VerplaatsHandlers;
  suggestedKennelIds?: Set<number>;
  bestKennelId?: number | null;
  onOpenDrawer?: (dierId: number) => void;
}) {
  const colour = resolveGroupColour(group, roomTypeByCode);
  const tiles = group.tiles ?? [];
  const bezet = group.bezet ?? 0;
  const totaal = group.totaal ?? tiles.length;

  return (
    <div className={`fawkes-kennelplanning__group fawkes-kennelplanning__group--${colour}`}>
      <div className="fawkes-kennelplanning__group-header">
        <span
          className={`fawkes-kennelplanning__group-type-badge fawkes-kennelplanning__group-type-badge--${colour}`}
        >
          {resolveTypeLabel(group, roomTypeByCode, t)}
        </span>
        <span className="fawkes-kennelplanning__group-title">{group.naam ?? ""}</span>
        <span className="fawkes-kennelplanning__group-dots">
          {tiles.map((tile, i) => (
            <span
              key={tile.kennelAsielId ?? i}
              className={
                "fawkes-kennelplanning__group-dot" +
                ((tile.occupied ?? (tile.occupantCount ?? 0) > 0)
                  ? " fawkes-kennelplanning__group-dot--filled"
                  : "")
              }
            />
          ))}
        </span>
        <span className="fawkes-kennelplanning__group-occupancy">
          {bezet}/{totaal}
        </span>
      </div>
      <div className="fawkes-kennelplanning__tiles-grid">
        {tiles.map((tile, i) => {
          const id = tile.kennelAsielId ?? null;
          const suggested = id != null && (suggestedKennelIds?.has(id) ?? false);
          const suggestedBest = id != null && bestKennelId != null && id === bestKennelId;
          return (
            <Tile
              key={tile.kennelAsielId ?? i}
              tile={tile}
              t={t}
              visibleFields={visibleFields}
              dnd={dnd}
              verplaats={verplaats}
              suggested={suggested}
              suggestedBest={suggestedBest}
              onOpenDrawer={onOpenDrawer}
            />
          );
        })}
      </div>
    </div>
  );
}

// ── Floorplan canvas ──────────────────────────────────────────────────────────────────────────────

function FloorplanCanvas({
  groups,
  roomTypeByCode,
  t,
  visibleFields,
  dnd,
  verplaats,
  suggestedKennelIds,
  bestKennelId,
  onOpenDrawer,
}: {
  groups: KennelGroup[];
  roomTypeByCode: Map<string, RoomType>;
  t: (k: string) => string;
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  verplaats?: VerplaatsHandlers;
  suggestedKennelIds?: Set<number>;
  bestKennelId?: number | null;
  onOpenDrawer?: (dierId: number) => void;
}) {
  if (groups.length === 0) {
    return <div className="fawkes-kennelplanning__empty">{t("kennelplanning.empty.noGroups")}</div>;
  }
  return (
    <>
      {groups.map((group, i) => (
        <GroupCard
          key={group.kennelGroepAsielId ?? i}
          group={group}
          roomTypeByCode={roomTypeByCode}
          t={t}
          visibleFields={visibleFields}
          dnd={dnd}
          verplaats={verplaats}
          suggestedKennelIds={suggestedKennelIds}
          bestKennelId={bestKennelId}
          onOpenDrawer={onOpenDrawer}
        />
      ))}
    </>
  );
}

// ── Pending strip (drop target for RELEASE; cards drag as source "pending") ────────────────────────

function PendingStrip({
  pending,
  t,
  visibleFields,
  dnd,
  verplaats,
  onAiTip,
  aiTipTargetId,
  onOpenDrawer,
}: {
  pending: PendingAnimal[];
  t: (k: string) => string;
  visibleFields: Set<string>;
  dnd?: DndHandlers;
  /** Tik-om-te-verplaatsen (alleen op telefoonformaat). */
  verplaats?: VerplaatsHandlers;
  /** kp-Epic 5: request placement suggestions for this pending animal's verblijf. */
  onAiTip?: (asielVerblijfId: number) => void;
  /** kp-Epic 5: the verblijf id whose suggestions are currently shown (for the active-button state). */
  aiTipTargetId?: number | null;
  /** kp-Epic 7: click a pending card → open the stay-history drawer for that dier. */
  onOpenDrawer?: (dierId: number) => void;
}) {
  const [dropActive, setDropActive] = useState(false);
  // Met een dier uit een hok in de hand is de strook de plek om het weer los te laten —
  // dezelfde betekenis als het er met de muis naartoe slepen.
  const losLatenHier = verplaats?.opgepakt != null && verplaats.opgepakt.bron === "kennel";
  // The whole strip container is the RELEASE drop target (only when editing is on).
  const dropProps = dnd
    ? {
        onDragOver: (e: ReactDragEvent) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = "move";
          if (!dropActive) setDropActive(true);
        },
        onDragLeave: () => setDropActive(false),
        onDrop: (e: ReactDragEvent) => {
          setDropActive(false);
          dnd.onPendingDrop(e);
        },
      }
    : {};
  return (
    <div
      className={
        "fawkes-kennelplanning__pending-strip-container" +
        (dropActive || losLatenHier
          ? " fawkes-kennelplanning__pending-strip-container--drop-active"
          : "")
      }
      onClick={losLatenHier ? () => verplaats!.losLaten() : undefined}
      {...dropProps}
    >
      <div className="fawkes-kennelplanning__pending-header">
        <span className="fawkes-kennelplanning__pending-strip-title">{t("kennelplanning.pending.title")}</span>
        {pending.length > 0 && (
          <span className="fawkes-kennelplanning__pending-counter">{pending.length}</span>
        )}
      </div>
      <div className="fawkes-kennelplanning__pending-strip">
        {pending.length === 0 ? (
          <span className="fawkes-kennelplanning__pending-empty">{t("kennelplanning.pending.dropHint")}</span>
        ) : (
          pending.map((p, i) => {
            const asielVerblijfId = p.asielVerblijfId ?? null;
            const draggable = dnd != null && asielVerblijfId != null;
            const dierId = p.dierId ?? null;
            const clickable =
              onOpenDrawer != null && dierId != null && verplaats?.opgepakt == null;
            const chips = renderOccupantFields(p, visibleFields, t);
            return (
            <div
              className={
                "fawkes-kennelplanning__pending-card" +
                (draggable ? " fawkes-kennelplanning__draggable" : "") +
                (clickable ? " fawkes-kennelplanning__clickable" : "")
              }
              key={p.asielVerblijfId ?? p.dierId ?? i}
              draggable={draggable || undefined}
              onDragStart={
                draggable
                  ? (e: ReactDragEvent) => dnd!.onOccupantDragStart(e, asielVerblijfId!, "pending")
                  : undefined
              }
              onClick={clickable ? () => onOpenDrawer!(dierId!) : undefined}
            >
              {visibleFields.has("PHOTO") && (
                <Avatar occupant={p} sizeClass="fawkes-kennelplanning__avatar--pending" />
              )}
              <div className="fawkes-kennelplanning__pending-info">
                {visibleFields.has("NAME") && (
                  <span className="fawkes-kennelplanning__pending-name">{p.naam ?? ""}</span>
                )}
                {chips.length > 0 && (
                  <span className="fawkes-kennelplanning__pending-id">{chips}</span>
                )}
                {visibleFields.has("FLAGS") && (
                  <FlagBadges occupant={p} className="fawkes-kennelplanning__pending-flags" />
                )}
              </div>
              {/* kp-Epic 5: AI tip — request placement suggestions for this animal. */}
              {onAiTip && asielVerblijfId != null && (
                <button
                  type="button"
                  className={
                    "fawkes-kennelplanning__pending-ai-tip" +
                    (aiTipTargetId === asielVerblijfId
                      ? " fawkes-kennelplanning__pending-ai-tip--active"
                      : "")
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    onAiTip(asielVerblijfId);
                  }}
                  title={t("kennelplanning.pending.aiTip")}
                >
                  {t("kennelplanning.pending.aiTip")}
                </button>
              )}
              <OppakKnop
                verplaats={verplaats}
                asielVerblijfId={asielVerblijfId}
                naam={p.naam ?? ""}
                bron="pending"
              />
            </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── List canvas ───────────────────────────────────────────────────────────────────────────────────

interface ListRow {
  group: KennelGroup;
  tile: KennelTile;
  occupant: Occupant | null;
}

function ListCanvas({
  groups,
  t,
  visibleFields,
  onOpenDrawer,
}: {
  groups: KennelGroup[];
  t: (k: string) => string;
  visibleFields: Set<string>;
  onOpenDrawer?: (dierId: number) => void;
}) {
  if (groups.length === 0) {
    return <div className="fawkes-kennelplanning__empty">{t("kennelplanning.empty.noGroups")}</div>;
  }
  const rows: ListRow[] = [];
  for (const group of groups) {
    for (const tile of group.tiles ?? []) {
      const occupants = tile.occupants ?? [];
      if (occupants.length === 0) {
        rows.push({ group, tile, occupant: null });
      } else {
        for (const occupant of occupants) rows.push({ group, tile, occupant });
      }
    }
  }

  // The fixed-identity columns (avatar/room/kennel/name) honour PHOTO/KENNEL_NUM/NAME; the trailing
  // "Velden" detail column reuses the shared chip builder for every other visible key.
  const showAvatar = visibleFields.has("PHOTO");
  const showKennel = visibleFields.has("KENNEL_NUM");
  const showName = visibleFields.has("NAME");

  return (
    <div className="fawkes-kennelplanning__list">
      <table className="fawkes-kennelplanning__list-table">
        <thead>
          <tr>
            {showAvatar && <th aria-label="avatar" className="fawkes-kennelplanning__list-th-avatar" />}
            <th>{t("kennelplanning.list.kamer")}</th>
            {showKennel && <th>{t("kennelplanning.list.kennel")}</th>}
            {showName && <th>{t("kennelplanning.list.naam")}</th>}
            <th>{t("kennelplanning.list.velden")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const o = row.occupant;
            const dierId = o?.dierId ?? null;
            const clickable = onOpenDrawer != null && dierId != null;
            const chips = o ? renderOccupantFields(o, visibleFields, t) : [];
            return (
              <tr
                key={`${row.tile.kennelAsielId ?? i}-${o?.asielVerblijfId ?? "empty"}-${i}`}
                className={clickable ? "fawkes-kennelplanning__list-row--clickable" : undefined}
                onClick={clickable ? () => onOpenDrawer!(dierId!) : undefined}
              >
                {showAvatar && (
                  <td className="fawkes-kennelplanning__list-td-avatar">
                    {o && <Avatar occupant={o} sizeClass="fawkes-kennelplanning__avatar--list" />}
                  </td>
                )}
                {/* data-label draagt de kolomkop mee de cel in, zoals DataTable dat doet: op een
                    telefoon wordt de rij daarmee een kaartje met bij elke waarde zijn kopje. */}
                <td data-label={t("kennelplanning.list.kamer")}>{row.group.naam ?? ""}</td>
                {showKennel && (
                  <td data-label={t("kennelplanning.list.kennel")}>{row.tile.kennelNummer ?? ""}</td>
                )}
                {showName && (
                  <td data-label={t("kennelplanning.list.naam")}>
                    {o ? o.naam ?? "" : t("kennelplanning.list.empty")}
                  </td>
                )}
                <td data-label={t("kennelplanning.list.velden")}>
                  {o ? (
                    <div className="fawkes-kennelplanning__list-fields">
                      {chips}
                      {visibleFields.has("FLAGS") && <FlagBadges occupant={o} />}
                    </div>
                  ) : (
                    !showName && t("kennelplanning.list.empty")
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Right capacity panel (read-only) ──────────────────────────────────────────────────────────────

/** kp-Epic 5: format the always-present 28-day basis line (handles the null/partial case). */
function projectionBasisLine(capacity: CapacityResponse | null, t: Translate): string {
  const intake = capacity?.prognoseIntake ?? 0;
  const release = capacity?.prognoseRelease ?? 0;
  const netto = capacity?.prognoseNetto ?? intake - release;
  const nettoLabel = netto > 0 ? `+${netto}` : String(netto);
  const trendRaw = (capacity?.prognoseTrend ?? "").toUpperCase();
  const trendLabel = ["STABIEL", "STIJGEND", "DALEND"].includes(trendRaw)
    ? t(`kennelplanning.capacity.projection.trend.${trendRaw}`)
    : trendRaw.toLowerCase();
  return t("kennelplanning.capacity.projection.basis", {
    args: [intake, release, nettoLabel, trendLabel],
  });
}

/** kp-Epic 5: the headline "days to full" state line (full / N days / no forecast). */
function projectionHeadline(capacity: CapacityResponse | null, t: Translate): string {
  const days = capacity?.prognoseDagenTotVol ?? null;
  if (days == null) return t("kennelplanning.capacity.projection.none");
  if (days === 0) return t("kennelplanning.capacity.projection.full");
  return t("kennelplanning.capacity.projection.days", { args: [days] });
}

function CapacityPanel({
  capacity,
  roomTypes,
  roomTypeByCode,
  collapsed,
  onToggle,
  t,
  onAddRoom,
  onEditRoom,
  onAddRoomType,
  onEditRoomType,
}: {
  capacity: CapacityResponse | null;
  roomTypes: RoomType[];
  roomTypeByCode: Map<string, RoomType>;
  collapsed: boolean;
  onToggle: () => void;
  t: Translate;
  /** kp-Epic 4: open the room dialog (create) / per-room edit / room-type create / room-type edit. */
  onAddRoom: () => void;
  onEditRoom: (room: RoomCapacityRow) => void;
  onAddRoomType: () => void;
  onEditRoomType: (rt: RoomType) => void;
}) {
  const bezet = capacity?.bezet ?? 0;
  const totaal = capacity?.totaal ?? 0;
  const percentage = capacity?.percentage ?? (totaal > 0 ? Math.round((bezet / totaal) * 100) : 0);
  const perGroep = capacity?.perGroep ?? [];

  return (
    <div className="fawkes-kennelplanning__panel-wrap">
      <button
        type="button"
        className="fawkes-kennelplanning__right-panel-toggle"
        onClick={onToggle}
        title={collapsed ? t("kennelplanning.rightPanel.expand") : t("kennelplanning.rightPanel.collapse")}
        aria-label={collapsed ? t("kennelplanning.rightPanel.expand") : t("kennelplanning.rightPanel.collapse")}
      >
        {collapsed ? <ChevronLeftIcon width={16} height={16} /> : <ChevronRightIcon width={16} height={16} />}
      </button>

      {!collapsed && (
        <div className="fawkes-kennelplanning__right-panel">
          <div className="fawkes-kennelplanning__panel-section-header">
            <h3 className="fawkes-kennelplanning__panel-title">{t("kennelplanning.capacity.title")}</h3>
            <span className="fawkes-kennelplanning__panel-subtitle">{t("kennelplanning.capacity.liveLabel")}</span>
          </div>

          <div className="fawkes-kennelplanning__capacity-summary">
            <span className="fawkes-kennelplanning__capacity-headline">
              {bezet} / {totaal}
            </span>
            <span className="fawkes-kennelplanning__capacity-percent">{percentage}%</span>
          </div>

          <div className="fawkes-kennelplanning__capacity-room-list">
            {/* kp-Epic 4: "Nieuwe ruimte" opens the room create dialog. */}
            <button
              type="button"
              className="fawkes-kennelplanning__capacity-add-room"
              onClick={onAddRoom}
            >
              <PlusCircleSolidIcon width={16} height={16} /> {t("kennelplanning.capacity.addRoom")}
            </button>
            {perGroep.map((room, i) => {
              const colour = resolveGroupColour(
                { ruimteTypeCode: room.ruimteTypeCode, ruimteTypeEnumId: room.ruimteTypeEnumId },
                roomTypeByCode,
              );
              return (
                <div className="fawkes-kennelplanning__capacity-row" key={room.kennelGroepAsielId ?? i}>
                  <span
                    className={`fawkes-kennelplanning__capacity-row-dot fawkes-kennelplanning__capacity-row-dot--${colour}`}
                  />
                  <span className="fawkes-kennelplanning__capacity-row-name">{room.naam ?? ""}</span>
                  <span className="fawkes-kennelplanning__capacity-row-value">
                    {room.bezet ?? 0}/{room.totaal ?? 0}
                  </span>
                  {/* kp-Epic 4: edit pencil — opens the room edit dialog (hover-revealed). */}
                  <button
                    type="button"
                    className="fawkes-kennelplanning__row-edit"
                    aria-label={t("kennelplanning.capacity.editRoom")}
                    title={t("kennelplanning.capacity.editRoom")}
                    onClick={() => onEditRoom(room)}
                  >
                    <PencilIcon width={12} height={12} />
                  </button>
                </div>
              );
            })}
          </div>

          {/* Prognose card (kp-Epic 5): headline "days to full" + the always-present 28-day basis
              line (renders even when the forecast itself is null). */}
          <div className="fawkes-kennelplanning__capacity-projection">
            <div className="fawkes-kennelplanning__projection-card">
              <span className="fawkes-kennelplanning__projection-title">
                {t("kennelplanning.capacity.projection.title")}
              </span>
              <span className="fawkes-kennelplanning__projection-body">
                {projectionHeadline(capacity, t)}
              </span>
              <span className="fawkes-kennelplanning__projection-basis">
                {projectionBasisLine(capacity, t)}
              </span>
            </div>
          </div>

          {/* Room-types list (read-only). */}
          <div className="fawkes-kennelplanning__panel-section-header">
            <h3 className="fawkes-kennelplanning__panel-title">{t("kennelplanning.roomTypes.title")}</h3>
            <span className="fawkes-kennelplanning__panel-subtitle">{t("kennelplanning.roomTypes.subtitle")}</span>
          </div>
          <div className="fawkes-kennelplanning__room-types-list">
            {/* kp-Epic 4: "Nieuw type" opens the room-type create dialog. */}
            <button
              type="button"
              className="fawkes-kennelplanning__capacity-add-room"
              onClick={onAddRoomType}
            >
              <PlusCircleSolidIcon width={16} height={16} /> {t("kennelplanning.roomTypes.addNew")}
            </button>
            {roomTypes.map((rt, i) => {
              const colour = normalizeColour(rt.kleur) ?? "green";
              return (
                <div
                  className={`fawkes-kennelplanning__room-type-row fawkes-kennelplanning__room-type-row--${colour}`}
                  key={rt.id ?? rt.code ?? i}
                >
                  <span
                    className={`fawkes-kennelplanning__room-type-dot fawkes-kennelplanning__capacity-row-dot--${colour}`}
                  />
                  <div className="fawkes-kennelplanning__room-type-textblock">
                    <span className="fawkes-kennelplanning__room-type-name">{rt.naam ?? ""}</span>
                  </div>
                  {/* kp-Epic 4: edit pencil — opens the room-type edit dialog (hover-revealed). */}
                  <button
                    type="button"
                    className="fawkes-kennelplanning__row-edit"
                    aria-label={t("kennelplanning.roomTypes.edit")}
                    title={t("kennelplanning.roomTypes.edit")}
                    onClick={() => onEditRoomType(rt)}
                  >
                    <PencilIcon width={12} height={12} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Toolbar ───────────────────────────────────────────────────────────────────────────────────────

const SPECIES: Array<{ key: SpeciesFilter; labelKey: string }> = [
  { key: "HONDEN", labelKey: "kennelplanning.species.honden" },
  { key: "KATTEN", labelKey: "kennelplanning.species.katten" },
  { key: "OVERIG", labelKey: "kennelplanning.species.overig" },
];

// Order + leading glyph mirror the Jmix `kennelplanning-overview-view.xml` toggle (List · Plattegrond ·
// Tijdlijn, each with a LIST/GRID/CHART icon). Default-active mode stays Plattegrond (see `viewMode` state).
const VIEW_MODES: Array<{ key: ViewMode; labelKey: string; icon: string }> = [
  { key: "LIST", labelKey: "kennelplanning.viewMode.list", icon: "☰" },
  { key: "FLOORPLAN", labelKey: "kennelplanning.viewMode.floorplan", icon: "▦" },
  { key: "TIMELINE", labelKey: "kennelplanning.viewMode.timeline", icon: "📊" },
];

// ── Page ──────────────────────────────────────────────────────────────────────────────────────────

export interface KennelplanningProps {
  /** Optional: open the full animal dossier (wired into the stay-history drawer). */
  onOpenAnimal?: (dierId: number) => void;
}

export function Kennelplanning({ onOpenAnimal }: KennelplanningProps = {}) {
  const t = useTranslate();
  const { isModuleEnabled } = useModuleAvailability();
  const moduleEnabled = isModuleEnabled("kennelplanning");

  const [viewMode, setViewMode] = useState<ViewMode>("FLOORPLAN");
  const [species, setSpecies] = useState<SpeciesFilter>("HONDEN");
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  // kp-Epic 4: which management dialog is open (and on what target). `null` target → create mode.
  // Keyed remount (`${mode}-${id}`) re-seeds the form fields per target.
  const [roomDialog, setRoomDialog] = useState<{ open: boolean; target: RoomEditTarget | null }>({
    open: false,
    target: null,
  });
  const [roomTypeDialog, setRoomTypeDialog] = useState<{
    open: boolean;
    target: RoomTypeEditTarget | null;
  }>({ open: false, target: null });

  // Today-only, read-only (kp-Epic 2). The date is rendered for parity but inert until kp-Epic 6.
  const today = useMemo(() => todayIso(), []);

  // ── kp-Epic 6: timeline date-bar + window state ──────────────────────────────────────────────────
  // DATE mode maps a single picked date to a window (date−3 … date+30, the computeTraceWindow margin).
  // PERIODE mode uses an explicit from…to (swapped if reversed). Default window: today−7 … today+30.
  const defaultFrom = useMemo(() => shiftISODate(today, -7), [today]);
  const defaultTo = useMemo(() => shiftISODate(today, 30), [today]);
  const [dateMode, setDateMode] = useState<DateMode>("PERIODE");
  const [pickedDate, setPickedDate] = useState<string>(today);
  const [rangeFrom, setRangeFrom] = useState<string>(defaultFrom);
  const [rangeTo, setRangeTo] = useState<string>(defaultTo);

  // The resolved [from,to] window + the highlighted day, derived from the active mode.
  const timelineWindow = useMemo<{ from: string; to: string; highlight: string }>(() => {
    if (dateMode === "DATE") {
      return {
        from: shiftISODate(pickedDate, -3),
        to: shiftISODate(pickedDate, 30),
        highlight: pickedDate,
      };
    }
    // PERIODE: swap if reversed so from <= to.
    const from = rangeFrom <= rangeTo ? rangeFrom : rangeTo;
    const to = rangeFrom <= rangeTo ? rangeTo : rangeFrom;
    return { from, to, highlight: today };
  }, [dateMode, pickedDate, rangeFrom, rangeTo, today]);

  // Time-travel tint when the window is not the default today-anchored one.
  const isDefaultWindow =
    dateMode === "PERIODE" && rangeFrom === defaultFrom && rangeTo === defaultTo;

  // ── kp-Epic 6: contact-trace state (a clicked bar sets the source dier) ───────────────────────────
  const [traceDierId, setTraceDierId] = useState<number | null>(null);
  const [traceLabel, setTraceLabel] = useState<string | null>(null);
  const startTrace = (dierId: number, dierNaam: string | null) => {
    setTraceDierId(dierId);
    setTraceLabel(dierNaam);
  };
  const closeTrace = () => {
    setTraceDierId(null);
    setTraceLabel(null);
  };

  // ── kp-Epic 7 (part 2): per-user visible-field set — drives every occupant chip ───────────────────
  // Default to the static DEFAULTS while the GET resolves; the field-settings drawer reconciles it from
  // the server and pushes changes back up via onChange so the chips re-render live.
  const [visibleFields, setVisibleFields] = useState<Set<string>>(
    () => new Set(DEFAULT_VISIBLE_FIELDS),
  );
  const [fieldSettingsOpen, setFieldSettingsOpen] = useState(false);

  // ── kp-Epic 7: stay-history drawer (clicking an occupant opens it for that dier) ───────────────────
  const [drawerDierId, setDrawerDierId] = useState<number | null>(null);
  const openDrawer = (dierId: number) => setDrawerDierId(dierId);
  const closeDrawer = () => setDrawerDierId(null);
  // "Besmettingsonderzoek" in the drawer: hand off to the kp-6 trace, switch to TIMELINE, close the drawer.
  const startTraceFromDrawer = (dierId: number, dierNaam: string | null) => {
    setDrawerDierId(null);
    startTrace(dierId, dierNaam);
    setViewMode("TIMELINE");
  };

  const overviewUrl = `kennelplanning/overview?datum=${today}&species=${species}`;
  const capacityUrl = `kennelplanning/capacity?species=${species}`;

  const {
    data: overviewData,
    isLoading: overviewLoading,
    isError: overviewError,
    refetch: refetchOverview,
  } = useCustom<OverviewResponse>({
    url: overviewUrl,
    method: "get",
    queryOptions: { enabled: moduleEnabled, retry: false },
  });
  const { data: roomTypesData, refetch: refetchRoomTypes } = useCustom<RoomType[]>({
    url: "kennelplanning/room-types",
    method: "get",
    queryOptions: { enabled: moduleEnabled, retry: false },
  });
  const { data: capacityData, refetch: refetchCapacity } = useCustom<CapacityResponse>({
    url: capacityUrl,
    method: "get",
    queryOptions: { enabled: moduleEnabled, retry: false },
  });

  // kp-Epic 7 (part 2): load the user's field-preferences once on mount. The server forces NAME +
  // KENNEL_NUM and drops unknown keys, so we always keep those two in the local set.
  const { data: fieldPrefsData } = useCustom<{ visibleFields?: string[] | null }>({
    url: "kennelplanning/field-preferences",
    method: "get",
    queryOptions: { enabled: moduleEnabled, retry: false },
  });
  useEffect(() => {
    const fields = fieldPrefsData?.data?.visibleFields;
    if (fields) {
      const next = new Set(fields);
      next.add("NAME");
      next.add("KENNEL_NUM");
      setVisibleFields(next);
    }
  }, [fieldPrefsData]);

  // ── kp-Epic 5: placement suggestions ────────────────────────────────────────────────────────────
  // The "+AI tip" button on a pending card sets the target verblijf; the fetch is keyed by the
  // url-with-query and only runs when a target is set. The result marks empty kennel tiles.
  const [suggestionTargetId, setSuggestionTargetId] = useState<number | null>(null);
  const suggestionsUrl = `kennelplanning/suggestions?asielVerblijfId=${suggestionTargetId ?? ""}`;
  const { data: suggestionsData } = useCustom<Suggestion[]>({
    url: suggestionsUrl,
    method: "get",
    queryOptions: { enabled: moduleEnabled && suggestionTargetId != null, retry: false },
  });

  const groups = overviewData?.data?.groepen ?? [];

  /**
   * De hokken voor het stickervel, in de volgorde waarin ze op het scherm staan.
   *
   * <p>Alleen hokken met een id: zonder id is er niets te scannen. Lege hokken krijgen wél een
   * sticker — die hangt aan de deur en niet aan het dier, en morgen ligt er iemand anders in.
   */
  const stickers = useMemo<HokSticker[]>(
    () =>
      groups.flatMap((g) =>
        (g.tiles ?? [])
          .filter((t): t is KennelTile & { kennelAsielId: number } => t.kennelAsielId != null)
          .map((t) => ({
            kennelAsielId: t.kennelAsielId,
            kennelNummer: t.kennelNummer ?? null,
            kamerNaam: g.naam ?? "",
          })),
      ),
    [groups],
  );
  const pending = overviewData?.data?.pendingAnimals ?? [];
  const roomTypes = useMemo(
    () =>
      (roomTypesData?.data ?? [])
        .slice()
        .sort((a, b) => (a.volgorde ?? 0) - (b.volgorde ?? 0)),
    [roomTypesData],
  );
  const roomTypeByCode = useMemo(() => {
    const map = new Map<string, RoomType>();
    for (const rt of roomTypes) if (rt.code) map.set(rt.code, rt);
    return map;
  }, [roomTypes]);
  const capacity = capacityData?.data ?? null;

  // kp-Epic 5: the suggestions for the active target, top-first (backend already returns top-3 desc).
  const suggestions = useMemo<Suggestion[]>(
    () => (suggestionTargetId != null ? suggestionsData?.data ?? [] : []),
    [suggestionsData, suggestionTargetId],
  );
  const suggestedKennelIds = useMemo(() => {
    const set = new Set<number>();
    for (const s of suggestions) if (s.kennelAsielId != null) set.add(s.kennelAsielId);
    return set;
  }, [suggestions]);
  const topSuggestion = suggestions[0] ?? null;
  const bestKennelId = topSuggestion?.kennelAsielId ?? null;
  const suggestionTargetName = useMemo(
    () => pending.find((p) => p.asielVerblijfId === suggestionTargetId)?.naam ?? null,
    [pending, suggestionTargetId],
  );

  const refreshAll = () => {
    void refetchOverview();
    void refetchRoomTypes();
    void refetchCapacity();
  };

  // ── kp-Epic 3: drag-drop placement (additive) ───────────────────────────────────────────────────
  // Editing is enabled only on TODAY and when no contact-trace is active. In this read shell those are
  // the defaults (the date is pinned to today and there is no trace state yet), so `editable` is true on
  // the FLOORPLAN view; later epics can tighten this guard. When not editable, no drag/drop is wired.
  const traceActive = traceDierId != null; // kp-Epic 6: a contact-trace is running
  const isToday = today === todayIso();
  const editable = viewMode === "FLOORPLAN" && isToday && !traceActive;

  const [dndNotice, setDndNotice] = useState<DndNotice>(null);
  const { mutate: mutatePlacement } = useCustomMutation();

  // After a successful write, re-read the overview (+capacity) rather than trust the small result body.
  function onPlacementSuccess(messageKey: string) {
    void refetchOverview();
    void refetchCapacity();
    setDndNotice({ kind: "ok", text: t(messageKey) });
  }

  // Error: show the server's Dutch message (422 policy / 409 conflict / 400 validation). The tone is
  // always "error"; the statusCode is folded into the message the dataProvider already surfaced.
  function onPlacementError(err: unknown) {
    const e = err as { message?: string; statusCode?: number };
    const text = e?.message?.trim() || t("kennelplanning.dnd.failed");
    setDndNotice({ kind: "error", text });
  }

  function placeAnimal(asielVerblijfId: number, kennelAsielId: number) {
    setDndNotice(null);
    mutatePlacement(
      {
        url: "kennelplanning/placements",
        method: "post",
        // `datum` is the board date being acted on — the server's today-guard rejects a non-today value.
        values: { asielVerblijfId, kennelAsielId, datum: today },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onPlacementSuccess("kennelplanning.dnd.placed"), onError: onPlacementError },
    );
  }

  function moveAnimal(asielVerblijfId: number, kennelAsielId: number) {
    setDndNotice(null);
    mutatePlacement(
      {
        url: `kennelplanning/placements/${asielVerblijfId}/move`,
        method: "put",
        values: { kennelAsielId, datum: today },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onPlacementSuccess("kennelplanning.dnd.moved"), onError: onPlacementError },
    );
  }

  function releaseAnimal(asielVerblijfId: number) {
    setDndNotice(null);
    mutatePlacement(
      {
        url: `kennelplanning/placements/${asielVerblijfId}/release`,
        method: "put",
        values: { datum: today },
        successNotification: false,
        errorNotification: false,
      },
      { onSuccess: () => onPlacementSuccess("kennelplanning.dnd.released"), onError: onPlacementError },
    );
  }

  // Which kennel a verblijf currently sits in — guards the no-op self-drop (MOVE only to a different kennel).
  function currentKennelOf(asielVerblijfId: number): number | null {
    for (const g of groups) {
      for (const tile of g.tiles ?? []) {
        for (const o of tile.occupants ?? []) {
          if (o.asielVerblijfId === asielVerblijfId) return tile.kennelAsielId ?? null;
        }
      }
    }
    return null;
  }

  // Tik-om-te-verplaatsen: overal waar met een vinger wordt bediend, want daar bestaat slepen
  // niet. Dat is dus niet alleen de telefoon maar ook de tablet — die is breed genoeg voor de
  // plattegrond en heeft evengoed geen muis om mee te slepen.
  const aanraak = useAanraken();
  const [opgepakt, setOpgepakt] = useState<
    { asielVerblijfId: number; naam: string; bron: "kennel" | "pending" } | null
  >(null);
  const verplaats: VerplaatsHandlers | undefined =
    aanraak && editable
      ? {
          opgepakt,
          oppakken: (asielVerblijfId, naam, bron) => {
            setDndNotice(null);
            setOpgepakt({ asielVerblijfId, naam, bron });
          },
          plaatsIn: (kennelAsielId) => {
            if (!opgepakt) return;
            if (opgepakt.bron === "pending") {
              placeAnimal(opgepakt.asielVerblijfId, kennelAsielId);
            } else if (currentKennelOf(opgepakt.asielVerblijfId) !== kennelAsielId) {
              moveAnimal(opgepakt.asielVerblijfId, kennelAsielId);
            }
            setOpgepakt(null);
          },
          losLaten: () => {
            if (opgepakt && opgepakt.bron === "kennel") releaseAnimal(opgepakt.asielVerblijfId);
            setOpgepakt(null);
          },
          annuleren: () => setOpgepakt(null),
          label: t("kennelplanning.verplaats.oppakken"),
        }
      : undefined;

  const dnd: DndHandlers | undefined = editable
    ? {
        onOccupantDragStart: (e, asielVerblijfId, source) =>
          setDragPayload(e, { asielVerblijfId, source }),
        onTileDrop: (e, kennelAsielId) => {
          e.preventDefault();
          const payload = readDragPayload(e);
          if (!payload) return;
          if (payload.source === "pending") {
            placeAnimal(payload.asielVerblijfId, kennelAsielId);
          } else if (currentKennelOf(payload.asielVerblijfId) !== kennelAsielId) {
            moveAnimal(payload.asielVerblijfId, kennelAsielId);
          }
          dragPayloadRef = null;
        },
        onPendingDrop: (e) => {
          e.preventDefault();
          const payload = readDragPayload(e);
          if (payload && payload.source === "kennel") {
            releaseAnimal(payload.asielVerblijfId);
          }
          dragPayloadRef = null;
        },
      }
    : undefined;

  // kp-Epic 5: toggle suggestions for a pending animal (clicking the same card's button clears them).
  const onAiTip = (asielVerblijfId: number) =>
    setSuggestionTargetId((prev) => (prev === asielVerblijfId ? null : asielVerblijfId));
  const clearSuggestions = () => setSuggestionTargetId(null);

  // ── kp-Epic 4: room + room-type management dialogs ───────────────────────────────────────────────
  const openCreateRoom = () => {
    setDndNotice(null);
    setRoomDialog({ open: true, target: null });
  };
  // The capacity row carries naam/ruimteTypeCode/totaal; positie/version come along when the feed
  // exposes them (forward-compatible — currently undefined, which the dialog handles).
  const openEditRoom = (room: RoomCapacityRow) => {
    if (room.kennelGroepAsielId == null) return;
    setDndNotice(null);
    const rt = room.ruimteTypeCode ? roomTypeByCode.get(room.ruimteTypeCode) : undefined;
    setRoomDialog({
      open: true,
      target: {
        kennelGroepAsielId: room.kennelGroepAsielId,
        naam: room.naam ?? null,
        ruimteTypeCode: room.ruimteTypeCode ?? null,
        aantalKennels: room.totaal ?? null,
        maxCapaciteit: rt?.maxCapaciteit ?? null,
        version: room.version ?? null,
        positie: room.positie ?? null,
      },
    });
  };
  const openCreateRoomType = () => {
    setDndNotice(null);
    setRoomTypeDialog({ open: true, target: null });
  };
  const openEditRoomType = (rt: RoomType) => {
    if (rt.id == null) return;
    setDndNotice(null);
    setRoomTypeDialog({
      open: true,
      target: {
        id: rt.id,
        naam: rt.naam ?? null,
        beschrijving: rt.beschrijving ?? null,
        kleur: rt.kleur ?? null,
        maxCapaciteit: rt.maxCapaciteit ?? null,
        version: rt.version ?? null,
      },
    });
  };
  const closeRoomDialog = () => setRoomDialog({ open: false, target: null });
  const closeRoomTypeDialog = () => setRoomTypeDialog({ open: false, target: null });
  // On a successful create/edit/delete: refetch the feeds and surface the result as an inline notice.
  const onDialogSaved = (message: string) => {
    refreshAll();
    setDndNotice({ kind: "ok", text: message });
  };
  const onRoomDialogSaved = (message: string) => {
    closeRoomDialog();
    onDialogSaved(message);
  };
  const onRoomTypeDialogSaved = (message: string) => {
    closeRoomTypeDialog();
    onDialogSaved(message);
  };

  // The hook is permissive while loading; only an explicit "disabled" hides the screen.
  if (!moduleEnabled) {
    return (
      <div className="fawkes-kennelplanning">
        <div className="fawkes-kennelplanning__empty">{t("kennelplanning.empty.moduleDisabled")}</div>
      </div>
    );
  }

  return (
    <div className="fawkes-kennelplanning">
      {/* ── Toolbar ── */}
      <div className="fawkes-kennelplanning__toolbar">
        <div className="fawkes-kennelplanning__view-toggle" role="group" aria-label={t("kennelplanning.title")}>
          {VIEW_MODES.map((m) => (
            <button
              key={m.key}
              type="button"
              className={
                "fawkes-kennelplanning__view-toggle-button" +
                (viewMode === m.key ? " fawkes-kennelplanning__view-toggle-button--active" : "")
              }
              aria-pressed={viewMode === m.key}
              onClick={() => {
                if (m.key !== "TIMELINE") closeTrace();
                setViewMode(m.key);
              }}
            >
              <span aria-hidden="true" className="fawkes-kennelplanning__view-toggle-icon">{m.icon}</span>
              {t(m.labelKey)}
            </button>
          ))}
        </div>

        <div className="fawkes-kennelplanning__species-pills" role="group" aria-label={t("kennelplanning.species.honden")}>
          {SPECIES.map((s) => (
            <button
              key={s.key}
              type="button"
              className={
                "fawkes-kennelplanning__species-pill" +
                (species === s.key ? " fawkes-kennelplanning__species-pill--active" : "")
              }
              aria-pressed={species === s.key}
              onClick={() => {
                closeTrace();
                setSpecies(s.key);
              }}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>

        <div className="fawkes-kennelplanning__toolbar-spacer" />

        {viewMode === "TIMELINE" ? (
          /* ── kp-Epic 6: Datum/Periode date-bar (drives the timeline window) ── */
          <div
            className={
              "fawkes-kennelplanning__date-bar" +
              (!isDefaultWindow ? " fawkes-kennelplanning__date-bar--time-travel" : "")
            }
          >
            <div
              className="fawkes-kennelplanning__date-mode-toggle"
              role="group"
              aria-label={t("kennelplanning.dateMode.dateLabel")}
            >
              <button
                type="button"
                className={
                  "fawkes-kennelplanning__date-mode-button" +
                  (dateMode === "DATE" ? " fawkes-kennelplanning__date-mode-button--active" : "")
                }
                aria-pressed={dateMode === "DATE"}
                onClick={() => setDateMode("DATE")}
              >
                {t("kennelplanning.dateMode.dateLabel")}
              </button>
              <button
                type="button"
                className={
                  "fawkes-kennelplanning__date-mode-button" +
                  (dateMode === "PERIODE" ? " fawkes-kennelplanning__date-mode-button--active" : "")
                }
                aria-pressed={dateMode === "PERIODE"}
                onClick={() => setDateMode("PERIODE")}
              >
                {t("kennelplanning.dateMode.periodLabel")}
              </button>
            </div>
            {dateMode === "DATE" ? (
              <DateField className="fawkes-kennelplanning__date-input"
                value={pickedDate}
                onChange={(e) => setPickedDate(e.target.value || today)}
                aria-label={t("kennelplanning.dateMode.dateLabel")}
              />
            ) : (
              <>
                <DateField className="fawkes-kennelplanning__date-input"
                  value={rangeFrom}
                  max={rangeTo}
                  onChange={(e) => setRangeFrom(e.target.value || defaultFrom)}
                  aria-label={t("kennelplanning.dateMode.from")}
                />
                <span className="fawkes-kennelplanning__date-sep">–</span>
                <DateField className="fawkes-kennelplanning__date-input"
                  value={rangeTo}
                  min={rangeFrom}
                  onChange={(e) => setRangeTo(e.target.value || defaultTo)}
                  aria-label={t("kennelplanning.dateMode.to")}
                />
              </>
            )}
            {!isDefaultWindow && (
              <span className="fawkes-kennelplanning__date-time-travel-badge">
                {t("kennelplanning.timeline.timeTravel")}
              </span>
            )}
          </div>
        ) : (
          <span className="fawkes-kennelplanning__date-label">
            {t("kennelplanning.dateMode.dateLabel")} · {t("kennelplanning.date.today")}
          </span>
        )}

        <button
          type="button"
          className="fawkes-kennelplanning__fieldsettings-button"
          onClick={() => setFieldSettingsOpen(true)}
          aria-label={t("kennelplanning.fieldSettings.open")}
          title={t("kennelplanning.fieldSettings.open")}
        >
          <GearIcon width={16} height={16} />
        </button>

        <Button
          variant="secondary"
          onClick={() =>
            printHokstickers({
              organisatie: t("kennelplanning.title"),
              stickers,
              datum: new Date().toLocaleDateString("nl-NL", {
                day: "numeric",
                month: "long",
                year: "numeric",
              }),
            })
          }
          disabled={overviewLoading || stickers.length === 0}
          title={t("kennelplanning.toolbar.stickersUitleg")}
        >
          {t("kennelplanning.toolbar.stickers")}
        </Button>

        <Button variant="secondary" onClick={refreshAll} disabled={overviewLoading}>
          {t("kennelplanning.toolbar.refresh")}
        </Button>
      </div>

      {/* ── DnD result notice (inline; mirrors the ReleaseWizard Notice — no toast provider) ── */}
      {dndNotice && (
        <div
          className={
            "fawkes-kennelplanning__dnd-notice" +
            (dndNotice.kind === "error"
              ? " fawkes-kennelplanning__dnd-notice--error"
              : " fawkes-kennelplanning__dnd-notice--ok")
          }
          role="alert"
        >
          <span>{dndNotice.text}</span>
          <button
            type="button"
            className="fawkes-kennelplanning__dnd-notice-close"
            aria-label={t("common.close")}
            onClick={() => setDndNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Pending strip (Floorplan only; drop target for RELEASE when editable) ── */}
      {viewMode === "FLOORPLAN" && (
        <PendingStrip
          pending={pending}
          t={t}
          visibleFields={visibleFields}
          dnd={dnd}
          verplaats={verplaats}
          onAiTip={onAiTip}
          aiTipTargetId={suggestionTargetId}
          onOpenDrawer={openDrawer}
        />
      )}

      {/* ── Wat je nu vasthebt, en hoe je het weer loslaat (alleen telefoon) ── */}
      {opgepakt && (
        <div className="fawkes-kennelplanning__verplaatsbalk" role="status">
          <span className="fawkes-kennelplanning__verplaatsbalk-tekst">
            {/* Een dier dat al in de wachtrij staat kun je daar niet nog eens neerzetten;
                alleen wie uit een hok komt kan er weer uit gehaald worden. */}
            {t(
              opgepakt.bron === "kennel"
                ? "kennelplanning.verplaats.kiesHokOfLos"
                : "kennelplanning.verplaats.kiesHok",
              { args: [opgepakt.naam] },
            )}
          </span>
          <button
            type="button"
            className="fawkes-kennelplanning__verplaatsbalk-stop"
            onClick={() => setOpgepakt(null)}
          >
            {t("common.cancel")}
          </button>
        </div>
      )}

      {/* ── kp-Epic 5: sticky suggestion banner (click anywhere or the X to clear) ── */}
      {suggestionTargetId != null && (
        <div className="fawkes-kennelplanning__suggestion-banner" role="status" onClick={clearSuggestions}>
          <span className="fawkes-kennelplanning__suggestion-banner-text">
            {topSuggestion
              ? t("kennelplanning.suggestion.banner", {
                  args: [
                    suggestionTargetName ?? "",
                    topSuggestion.kennelNummer ?? "",
                    topSuggestion.roomNaam ?? "",
                    topSuggestion.score ?? 0,
                  ],
                })
              : t("kennelplanning.suggestion.none")}
          </span>
          <button
            type="button"
            className="fawkes-kennelplanning__suggestion-banner-close"
            aria-label={t("kennelplanning.suggestion.clear")}
            onClick={(e) => {
              e.stopPropagation();
              clearSuggestions();
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Canvas row: canvas + capacity panel ── */}
      <div className="fawkes-kennelplanning__canvas-row">
        <div className="fawkes-kennelplanning__canvas">
          {viewMode === "TIMELINE" ? (
            /* kp-Epic 6: the timeline owns its own fetch/loading/error/empty states. */
            <KennelTimeline
              species={species}
              from={timelineWindow.from}
              to={timelineWindow.to}
              highlightDate={timelineWindow.highlight}
              enabled={moduleEnabled}
              traceDierId={traceDierId}
              onBarClick={startTrace}
              onCloseTrace={closeTrace}
              traceLabel={traceLabel}
              t={t}
            />
          ) : overviewLoading ? (
            <div className="fawkes-kennelplanning__empty">{t("common.loading")}</div>
          ) : overviewError ? (
            <div className="fawkes-kennelplanning__empty">{t("kennelplanning.loadFailed")}</div>
          ) : viewMode === "LIST" ? (
            <ListCanvas
              groups={groups}
              t={t}
              visibleFields={visibleFields}
              onOpenDrawer={openDrawer}
            />
          ) : (
            <FloorplanCanvas
              groups={groups}
              roomTypeByCode={roomTypeByCode}
              t={t}
              visibleFields={visibleFields}
              dnd={dnd}
              verplaats={verplaats}
              suggestedKennelIds={suggestedKennelIds}
              bestKennelId={bestKennelId}
              onOpenDrawer={openDrawer}
            />
          )}
        </div>

        <CapacityPanel
          capacity={capacity}
          roomTypes={roomTypes}
          roomTypeByCode={roomTypeByCode}
          collapsed={panelCollapsed}
          onToggle={() => setPanelCollapsed((c) => !c)}
          t={t}
          onAddRoom={openCreateRoom}
          onEditRoom={openEditRoom}
          onAddRoomType={openCreateRoomType}
          onEditRoomType={openEditRoomType}
        />
      </div>

      {/* ── kp-Epic 7: stay-history drawer (opens on occupant/pending/list click) ── */}
      <Drawer
        open={drawerDierId != null}
        onClose={closeDrawer}
        ariaLabel={t("kennelplanning.historyDrawer.title")}
        closeLabel={t("common.close")}
      >
        {drawerDierId != null && (
          <StayHistoryDrawer
            dierId={drawerDierId}
            enabled={moduleEnabled}
            onStartTrace={startTraceFromDrawer}
            onOpenAnimal={onOpenAnimal}
            t={t}
          />
        )}
      </Drawer>

      {/* ── kp-Epic 7 (part 2): field-settings drawer (preference-driven chips) ── */}
      <FieldSettingsDrawer
        open={fieldSettingsOpen}
        onClose={() => setFieldSettingsOpen(false)}
        enabled={moduleEnabled}
        onChange={setVisibleFields}
        t={t}
      />

      {/* ── kp-Epic 4: management dialogs (keyed remount per target re-seeds the form) ── */}
      {roomDialog.open && (
        <KennelGroepFormDialog
          key={`room-${roomDialog.target?.kennelGroepAsielId ?? "new"}`}
          target={roomDialog.target}
          t={t}
          onClose={closeRoomDialog}
          onSaved={onRoomDialogSaved}
        />
      )}
      {roomTypeDialog.open && (
        <RuimteTypeFormDialog
          key={`roomType-${roomTypeDialog.target?.id ?? "new"}`}
          target={roomTypeDialog.target}
          t={t}
          onClose={closeRoomTypeDialog}
          onSaved={onRoomTypeDialogSaved}
        />
      )}
    </div>
  );
}
